import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCurrency } from '@/lib/utils';
import { TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Stats {
  monto_total: number;
  monto_pendiente: number;
  monto_aprobado: number;
}

export type AmountFilter = 'pendiente' | 'aprobado' | null;

interface AmountSummaryProps {
  stats?: Stats;
  isLoading: boolean;
  activeFilter?: AmountFilter;
  onFilterClick?: (filter: AmountFilter) => void;
}

export function AmountSummary({ stats, isLoading, activeFilter, onFilterClick }: AmountSummaryProps) {
  const handleClick = (filter: AmountFilter) => {
    if (!onFilterClick) return;
    // Toggle: si ya está activo, desactivar
    onFilterClick(activeFilter === filter ? null : filter);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Monto Total en Sistema
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-8 w-32 bg-gray-200 animate-pulse rounded" />
          ) : (
            <>
              <p className="text-2xl font-bold">{formatCurrency(stats?.monto_total || 0)}</p>
              <p className="text-xs text-muted-foreground">COP</p>
            </>
          )}
        </CardContent>
      </Card>

      <Card
        className={cn(
          'cursor-pointer transition-all hover:shadow-md',
          activeFilter === 'aprobado' && 'ring-2 ring-green-500 shadow-md'
        )}
        onClick={() => handleClick('aprobado')}
      >
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Monto Aprobado
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-8 w-32 bg-gray-200 animate-pulse rounded" />
          ) : (
            <>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(stats?.monto_aprobado || 0)}
              </p>
              <p className="text-xs text-muted-foreground">COP</p>
            </>
          )}
        </CardContent>
      </Card>

      <Card
        className={cn(
          'cursor-pointer transition-all hover:shadow-md',
          activeFilter === 'pendiente' && 'ring-2 ring-yellow-500 shadow-md'
        )}
        onClick={() => handleClick('pendiente')}
      >
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Monto Pendiente de Aprobar
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-8 w-32 bg-gray-200 animate-pulse rounded" />
          ) : (
            <>
              <p className="text-2xl font-bold text-yellow-600">
                {formatCurrency(stats?.monto_pendiente || 0)}
              </p>
              <p className="text-xs text-muted-foreground">COP</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
